/**
 * gridFrequency — site grid frequency resolution + OEM-case compatibility.
 *
 * Origin: the Redhead on-site power report review (2026-07-27). The OEM
 * register's largest frames carry structured `frequencyHz` (e.g. the GE
 * 9HA.01 case is 50 Hz-only), but nothing compared that against the site's
 * grid, so a 50 Hz frame anchored a 60 Hz Louisiana screening un-flagged.
 * These helpers close the gap at the equipment Finder / import bridge —
 * derivation-time only; frozen scenarios are never rewritten.
 *
 * The site's expected frequency comes from its country. Sites carry no
 * structured country today, so {@link expectedGridFrequencyHzFromAddress}
 * reads the geocoder-derived trailing country token of the site address
 * ("… Moss Bluff, LA, USA" → USA → 60). That heuristic is deliberately
 * conservative: an unrecognized or absent country resolves to null
 * ('unknown-site'), which suppresses the hard mismatch warning rather than
 * guessing one. Countries with split grids (e.g. Japan) are intentionally
 * absent from the map for the same reason.
 */

export type SiteGridFrequencyHz = 50 | 60;

/**
 * Unambiguous country → grid frequency map, keyed by the country tokens the
 * platform's geocoded addresses actually end with (plus ISO-alpha forms).
 * Extend as the platform enters new markets; never add split-grid countries.
 */
const GRID_FREQUENCY_BY_COUNTRY_TOKEN: Readonly<Record<string, SiteGridFrequencyHz>> = {
  // 60 Hz
  'us': 60, 'usa': 60, 'united states': 60, 'united states of america': 60,
  'ca': 60, 'canada': 60,
  'mx': 60, 'mexico': 60, 'méxico': 60,
  'puerto rico': 60,
  'south korea': 60, 'korea': 60,
  'philippines': 60,
  'taiwan': 60,
  'brazil': 60,
  // 50 Hz
  'uk': 50, 'gb': 50, 'united kingdom': 50,
  'ireland': 50,
  'germany': 50, 'deutschland': 50,
  'france': 50,
  'spain': 50, 'españa': 50,
  'italy': 50, 'italia': 50,
  'netherlands': 50,
  'poland': 50,
  'portugal': 50,
  'sweden': 50, 'norway': 50, 'denmark': 50, 'finland': 50,
  'australia': 50,
  'new zealand': 50,
  'india': 50,
  'singapore': 50,
  'south africa': 50,
  'united arab emirates': 50,
};

/** Resolve a country token/name/code to its grid frequency; null = unknown. */
export function expectedGridFrequencyHzForCountry(
  country: string | null | undefined,
): SiteGridFrequencyHz | null {
  if (typeof country !== 'string') return null;
  const normalized = country.trim().toLowerCase().replace(/\.$/, '');
  return GRID_FREQUENCY_BY_COUNTRY_TOKEN[normalized] ?? null;
}

/**
 * Resolve a site's grid frequency from its geocoded address by reading the
 * trailing comma-separated token as the country. Null when the token is not
 * an unambiguous single-grid country — callers must treat null as "unknown",
 * never default it.
 */
export function expectedGridFrequencyHzFromAddress(
  address: string | null | undefined,
): SiteGridFrequencyHz | null {
  if (typeof address !== 'string' || address.trim().length === 0) return null;
  const tokens = address.split(',').map(token => token.trim()).filter(Boolean);
  if (tokens.length === 0) return null;
  return expectedGridFrequencyHzForCountry(tokens[tokens.length - 1]);
}

export type GridFrequencyFit =
  /** The OEM case is published for the site's grid frequency. */
  | 'compatible'
  /** The OEM case cannot serve the site's grid frequency as published. */
  | 'incompatible'
  /** The OEM case does not state grid frequency ([]); confirm with the OEM. */
  | 'unstated'
  /** The site's grid frequency could not be resolved; no verdict is made. */
  | 'unknown-site';

/** Compare an OEM case's published frequencies against the site grid. */
export function evaluateGridFrequencyFit(
  machineFrequencyHz: readonly number[] | null | undefined,
  siteGridFrequencyHz: SiteGridFrequencyHz | null | undefined,
): GridFrequencyFit {
  if (siteGridFrequencyHz == null) return 'unknown-site';
  if (!Array.isArray(machineFrequencyHz) || machineFrequencyHz.length === 0) return 'unstated';
  return machineFrequencyHz.includes(siteGridFrequencyHz) ? 'compatible' : 'incompatible';
}

/**
 * One shared caveat sentence per fit state, so the Finder, the import
 * prefill, and the persisted entry notes all speak identically. Null when
 * no caveat is warranted (compatible / unknown site).
 */
export function gridFrequencyCaveat(
  fit: GridFrequencyFit,
  machineFrequencyHz: readonly number[] | null | undefined,
  siteGridFrequencyHz: SiteGridFrequencyHz | null | undefined,
): string | null {
  if (fit === 'incompatible' && siteGridFrequencyHz != null) {
    const published = [...(machineFrequencyHz ?? [])].join('/');
    return `Grid-frequency mismatch: this OEM case is published for ${published} Hz only and the site grid `
      + `is ${siteGridFrequencyHz} Hz — the machine cannot serve this site as published. Select the OEM's `
      + `${siteGridFrequencyHz} Hz frame equivalent; class-level capacity screening remains representative.`;
  }
  if (fit === 'unstated' && siteGridFrequencyHz != null) {
    return `Grid-frequency compatibility is not stated in the OEM case; confirm ${siteGridFrequencyHz} Hz `
      + 'capability with the OEM.';
  }
  return null;
}
